/*
 * Copyright 2024 NewMedia Centre - Delft University of Technology
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

'use server'

// import dependencies
import { z } from 'zod'
import { webAppManageProcedure } from './procedures'
import { prisma } from '@/db'
import { Scope } from '@prisma/client'

/** Get all the permissions for the web app */
export const getPermissionsAction = webAppManageProcedure.createServerAction()
	.handler(async ({ ctx }) => {
		return await prisma.webAppPermission.findMany({
			where: {
				webAppId: ctx.webApp.id
			},
			include: {
				user: true
			}
		})
	})

/** Gives a user permissions for the web app */
export const addPermissionAction = webAppManageProcedure.createServerAction()
	.input(z.object({
		email: z.string().email(),
	}))
	.handler(async ({ input, ctx }) => {
		return await prisma.webAppPermission.create({
			data: {
				user: {
					connect: {
						email: input.email
					}
				},
				webApp: {
					connect: {
						id: ctx.webApp.id
					}
				}
			}
		})
	})

/** Updates the permissions of a specific user */
export const updatePermissionAction = webAppManageProcedure.createServerAction()
	.input(z.object({
		userId: z.string(),
		editApp: z.boolean().optional(),
		manageApp: z.boolean().optional(),
		readData: z.boolean().optional(),
		writeData: z.boolean().optional(),
		manageData: z.boolean().optional()
	}))
	.handler(async ({ input, ctx }) => {
		return await prisma.webAppPermission.update({
			where: {
				userId_webAppId: {
					userId: input.userId,
					webAppId: ctx.webApp.id
				}
			},
			data: {
				...(input.editApp !== undefined && { editApp: input.editApp }),
				...(input.manageApp !== undefined && { manageApp: input.manageApp }),
				...(input.readData !== undefined && { readData: input.readData }),
				...(input.writeData !== undefined && { writeData: input.writeData }),
				...(input.manageData !== undefined && { manageData: input.manageData })
			}
		})
	})

/** Sets the scope of who is allowed to write user data to this web app */
export const updateWriteScopeAction = webAppManageProcedure.createServerAction()
	.input(z.object({
		scope: z.nativeEnum(Scope).optional()
	}))
	.handler(async ({ input, ctx }) => {
		return await prisma.webApp.update({
			where: {
				id: ctx.webApp.id
			},
			data: {
				writeData: input.scope
			}
		})
	})

/** Removes the permission of a specific user */
export const deletePermissionAction = webAppManageProcedure.createServerAction()
	.input(z.object({
		userId: z.string()
	}))
	.handler(async ({ input, ctx }) => {
		return await prisma.webAppPermission.delete({
			where: {
				userId_webAppId: {
					userId: input.userId,
					webAppId: ctx.webApp.id
				}
			}
		})
	})